import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkAdminAccess, getAllUsersAdmin, getUserStatsAdmin, requireAdminAccess } from '../../utils/adminService';
import { Users, TrendingUp, CheckCircle, Clock, User as UserIcon, FileText, ChevronUp, ChevronDown } from 'lucide-react';
import { AdminLayout, AdminStatCard, AdminAccessDenied } from '../../components/admin/layout';

const AdminUserManager = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: 'updated_at',
    direction: 'desc'
  });

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const adminAccess = await checkAdminAccess();
      setIsAdmin(adminAccess);
      
      if (adminAccess) {
        await loadData();
      } else {
        setAccessDenied(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('권한 확인 실패:', error);
      setAccessDenied(true);
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      // 관리자 권한 재확인
      await requireAdminAccess();
      
      console.log('=== 관리자 권한으로 데이터 로딩 시작 ===');
      
      const [usersData, statsData] = await Promise.all([
        getAllUsersAdmin(),
        getUserStatsAdmin()
      ]);
      
      setUsers(usersData);
      setStats(statsData);
    } catch (error) {
      console.error('데이터 로딩 실패:', error);
      if (error.message?.includes('Admin privileges required')) {
        setAccessDenied(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewUserResumes = (user) => {
    navigate(`/admin-users/${user.id}/resumes`);
  };

  const formatDateTimeDetailed = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = React.useMemo(() => {
    let sortableUsers = [...users];
    if (sortConfig.key) {
      sortableUsers.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // null 값 처리
        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';
        
        // 날짜 필드인 경우 Date 객체로 변환
        if (['created_at', 'updated_at', 'last_sign_in_at'].includes(sortConfig.key)) {
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [users, sortConfig]);

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-400">⇅</span>;
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronUp className="inline h-4 w-4" /> : 
      <ChevronDown className="inline h-4 w-4" />;
  };

  if (accessDenied) {
    return <AdminAccessDenied />;
  }

  return (
    <AdminLayout
      title="사용자 관리"
      description="Latte is 사용자들의 활동 현황과 통계를 확인합니다."
      icon={<Users className="h-8 w-8 text-blue-600" />}
      isAdmin={isAdmin}
      loading={loading}
    >
      {/* 통계 카드들 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <AdminStatCard
          icon={<Users />}
          title="전체 가입자"
          value={stats?.totalUsers || 0}
          subtitle={`프로필 생성: ${stats?.usersWithProfiles || 0}`}
          iconColorClass="text-blue-600"
        />
        
        <AdminStatCard
          icon={<Clock />}
          title="진행중인 세션"
          value={stats?.inProgressSessions || 0}
          subtitle={`활성: ${stats?.activeSessions || 0}`}
          iconColorClass="text-yellow-600"
        />
        
        <AdminStatCard
          icon={<CheckCircle />}
          title="완료된 세션"
          value={stats?.completedSessions || 0}
          subtitle="진행률 100% 달성"
          iconColorClass="text-green-600"
        />
        
        <AdminStatCard
          icon={<TrendingUp />}
          title="이번 주 신규"
          value={stats?.newUsersThisWeek || 0}
          subtitle={`오늘: ${stats?.newUsersToday || 0}`}
          iconColorClass="text-purple-600"
        />
      </div>

      {/* 사용자 목록 테이블 */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">사용자 목록</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  사용자
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  이메일 상태
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  세션 수
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center">
                    가입일
                    <SortIcon columnKey="created_at" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('updated_at')}
                >
                  <div className="flex items-center">
                    마지막 업데이트
                    <SortIcon columnKey="updated_at" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('last_sign_in_at')}
                >
                  <div className="flex items-center">
                    마지막 로그인
                    <SortIcon columnKey="last_sign_in_at" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  작업
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {user.avatar_url ? (
                        <img 
                          className="h-10 w-10 rounded-full" 
                          src={user.avatar_url} 
                          alt={user.name}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center"
                        style={{ display: user.avatar_url ? 'none' : 'flex' }}
                      >
                        <UserIcon className="h-5 w-5 text-gray-500" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.name || '이름 없음'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      {user.email_confirmed_at ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          인증완료
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          미인증
                        </span>
                      )}
                      {user.profile_data && (
                        <div className="text-xs text-gray-500 mt-1">
                          프로필 생성
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <span className="font-medium">{user.session_stats?.totalSessions || 0}</span>
                      <div className="text-xs text-gray-500">
                        활성: {user.session_stats?.activeSessions || 0} | 완료: {user.session_stats?.completedSessions || 0}
                      </div>
                      {user.session_stats?.activeSessions > 0 && user.session_stats?.latestActiveProgress !== undefined && (
                        <div className="text-xs mt-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            진행률: {user.session_stats.latestActiveProgress}%
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTimeDetailed(user.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTimeDetailed(user.updated_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTimeDetailed(user.last_sign_in_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleViewUserResumes(user)}
                      className="inline-flex items-center px-3 py-1.5 border border-green-600 text-green-600 bg-white rounded-md hover:bg-green-50 transition-colors"
                    >
                      <FileText className="h-4 w-4 mr-1.5" />
                      이력서 보기
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUserManager;
